"use client";

import { ArrowDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Expert() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const {
    ref: p1Ref,
    isVisible: p1Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const {
    ref: p2Ref,
    isVisible: p2Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const {
    ref: linkRef,
    isVisible: linkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="14627" id="your-dedicated-expert" className="text-foreground py-24 md:py-36 font-default light bg-background">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div ref={eyebrowRef} className="text-primary mb-8 transition-all duration-700 ease-out" style={{
            opacity: eyebrowVisible ? 1 : 0,
            transform: eyebrowVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "0ms"
          }}>
              <span className="text-[0.75rem] font-bold uppercase" style={{
              letterSpacing: "0.2em",
              lineHeight: 1.5
            }}>
                Your Dedicated Expert
              </span>
            </div>

            <h1 ref={headlineRef} className="font-default text-foreground transition-all duration-700 ease-out" style={{
            fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
            fontWeight: 400,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "80ms"
          }}>
              Refined beauty through{" "}
              <em className="font-serif not-italic" style={{
              fontStyle: "italic",
              color: "var(--primary)",
              opacity: 0.92
            }}>
                precision
              </em>
              .
            </h1>

            <div className="mt-10 space-y-7 max-w-[56ch]">
              <p ref={p1Ref} className="text-foreground/85 transition-all duration-700 ease-out" style={{
              fontSize: "clamp(1.125rem, 1.2vw, 1.35rem)",
              fontWeight: 400,
              letterSpacing: "0",
              lineHeight: 1.6,
              opacity: p1Visible ? 1 : 0,
              transform: p1Visible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: "160ms"
            }}>&nbsp;Ideal Image Aesthetics and Wellness, LLC was founded on the belief that facial balancing is an art form. We provide a private, concierge experience that removes the stress of traditional clinical visits, offering you a sanctuary where your natural proportions are restored and enhanced with expert care.</p>

              <p ref={p2Ref} className="text-foreground/85 transition-all duration-700 ease-out" style={{
              fontSize: "clamp(1.125rem, 1.2vw, 1.35rem)",
              fontWeight: 400,
              letterSpacing: "0",
              lineHeight: 1.6,
              opacity: p2Visible ? 1 : 0,
              transform: p2Visible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: "240ms"
            }}>From preventative filler for younger patients to sophisticated Botox treatments, we focus on subtle, effortless results. Dr. Ojeda, DNP, APRN, FNP-C combines clinical precision with a gentle touch, ensuring every treatment supports your unique beauty without ever looking overdone.</p>
            </div>

            <div ref={linkRef} className="mt-10 transition-all duration-700 ease-out" style={{
            opacity: linkVisible ? 1 : 0,
            transform: linkVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "320ms"
          }}>
              <Link to="/AboutPage" className="group inline-flex items-center gap-2 text-foreground font-medium" style={{
              letterSpacing: "0.01em"
            }}><span>
                  <em className="font-serif !w-[274.953px] !h-[23px]" style={{
                  fontStyle: "italic",
                  color: "var(--primary)",
                  opacity: 0.92
                }}>Meet Dr. Ojeda, FNP</em>
                  <span className="ml-1">below</span>
                </span>
                <ArrowDown className="size-4 text-primary transition-transform duration-300 ease-out group-hover:translate-y-0.5" strokeWidth={1.5} />
                <span className="absolute left-0 right-0 -bottom-1 h-[1.5px] bg-primary transition-all duration-300 ease-out" style={{
                display: "none"
              }} /></Link>
              <div className="relative mt-1 h-[1.5px] w-[60%] bg-primary transition-all duration-300 ease-out group-hover:w-full" aria-hidden="true" />
            </div>
          </div>

          {}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div ref={imgRef} className="relative overflow-hidden rounded-3xl transition-all ease-out" style={{
            aspectRatio: "4 / 5",
            opacity: imgVisible ? 1 : 0,
            transform: imgVisible ? "translateY(0)" : "translateY(16px)",
            transitionDuration: "900ms",
            transitionDelay: "400ms"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/concierge-founder-garden-ridge.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/concierge-founder-garden-ridge.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728549/img/concierge-founder-garden-ridge.webp-wvc-sizes" alt="Dr. Amber Ojeda providing a personalized consultation" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.02]" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}